import { atsStore, lookupStore } from "../stores";
import { Constants, Enum } from "../helpers";
import { convertBase64toFileObject } from "../helpers/FileProcessing";
import { action, makeObservable, observable } from "mobx";

let inputAtsFields = [
  'candidate_address1',
  'address1',
  'candidate_address2',
  'address2',
  'candidate_city',
  'city',
  'candidate_suburb',
  'suburb',
  'candidate_current_employer',
  'current_employer',
  'candidate_first_name',
  'first_name',
  'candidate_last_name',
  'last_name',
  'candidate_current_job_title',
  'current_job_title',
  'candidate_state',
  'state',
  'candidate_desired_job_title',
  'candidate_zip',
  'zip',
  'candidate_emergency_contact',
];

let dropDownAtsFields = [
  'candidate_country',
  'country',
  'candidate_current_work_type',
  'current_work_type',
  'candidate_desired_pay_frequency',
  'desired_pay_frequency',
  'candidate_current_pay_frequency',
  'current_pay_frequency',
  'candidate_desired_work_type',
  'candidate_skill_category',
  'candidate_skill_subcategory',
  'candidate_skill',
  'candidate_notice_period',
];

let checkboxesAtsFields = [
  'candidate_skill_category',
  'candidate_skill_subcategory',
  'candidate_skill',
];

export const phoneAtsFields = [
  'candidate_mobile',
  'mobile',
  'candidate_emergency_phone'
];

export const fileUploadAtsFields = [
  // 'candidate_photo',
  'candidate_resume'
];

export const numberAtsFields = [
  'candidate_desired_low_salary',
  'desired_low_salary',
  'candidate_desired_high_salary',
  'desired_high_salary',
  'candidate_current_salary',
  'current_salary'
];

export const emailAtsFields = [
  'candidate_email',
  'email'
];

export const scaleAtsFields = [
  'candidate_nps'
];

export const dateAtsFields = [
  'candidate_availability_date',
  'availability.date'
];

export const linkedinAtsFields = [
  'candidate_linkedin',
  'linkedin'
];

/**
 * Extracts the actual slot name from a potentially alias-prefixed field name.
 *
 * When a Tripetto section has an alias, Export.exportables() prefixes the
 * slot name with the section alias + " / " separator.
 * e.g. "Alias1 / ats-candidate_state" → "ats-candidate_state"
 *
 * Confirmed by live submission data.
 */
const getSlotName = (name) => {
  if (!name) return name;
  // Tripetto uses " / " as the default separator between section alias and slot name
  const parts = name.split(' / ');
  return parts[parts.length - 1];
};

export const isNameHasAts = (name) => {
  // Strip any section alias prefix before checking for "ats-" prefix
  // e.g. "Alias1 / ats-candidate_state" → "ats-candidate_state"
  const slotName = getSlotName(name);
  // check if have ats on first data before (-),, example "ats-first_name-textinline"
  if (slotName?.split('-', 3)[0] === "ats") {
    return true;
  }

  return false;
};

export const getAtsTokenName = (name) => {
  // Strip any section alias prefix before extracting the ATS token
  const slotName = getSlotName(name);

  // if the name has no 'ats-' just return undefined
  if (!slotName?.startsWith("ats-")) return undefined;

  // return only {token} for rule ats-{token}-{fieldId}
  const matchWithNumber = slotName.match(/^ats-(.*)-(\d+)$/);
  if (matchWithNumber) return matchWithNumber[1];

  // return only {token} for rule ats-{token}
  const matchSimple = slotName.match(/^ats-(.+)$/);
  return matchSimple ? matchSimple[1] : undefined;
};

export const splitCheckboxesReference = (data) => {
  const checkboxesReference = data.split("\n");
  const referenceData = checkboxesReference[1].split("-");
  const field = referenceData[1] || "";
  const fieldId = referenceData[referenceData.length - 1];

  // Check if checkboxes are candidate_skill_category, candidate_skill_subcategory, or candidate_skill
  if (isSkillField(field)) {
    const values = [];
    checkboxesReference.forEach(ref => {
      if (ref.trim() !== "") {
        const referenceData = ref.split("-");
        const value = referenceData[referenceData.length - 1];
        
        // Only add valid numeric fieldIds and avoid duplicates
        if (value && !isNaN(value) && !values.includes(value)) {
          values.push(value);
        }
      }
    });
    return { atsField: field, atsFieldId: undefined, atsValue: values };
  }

  const customFieldData = tripettoStore.listCustomFields().filter(item => item.fieldId === parseInt(fieldId));

  const atsData = {
    atsField: customFieldData.length > 0 ? customFieldData[0].tokenVariable.replace("-" + fieldId, "") : "",
    atsFieldId: fieldId,
    atsValue: undefined
  }
  return atsData
};

const isSkillField = (field) => {
  return ["candidate_skill_category", "candidate_skill_subcategory", "candidate_skill"].includes(field);
};

// Matches {{merge_tag}} tokens embedded in survey question prompt or help text.
const MERGE_TAG_PATTERN = /{{\s*([A-Za-z0-9_][A-Za-z0-9_\-. ]*?)\s*}}/g;

// Replaces merge tags in the survey definition with the recipient values resolved
// by the backend for this survey token. Display-only: the substituted definition is
// only rendered, never written back. Tags without a value render as empty text.
export const substituteMergeTags = (definition, mergeTags) => {
  if (!definition) return definition;

  const values = mergeTags || {};
  const json = JSON.stringify(definition).replace(MERGE_TAG_PATTERN, (match, token) => {
    const value = Object.prototype.hasOwnProperty.call(values, token) ? values[token] : "";
    // escape the replacement for a JSON string context
    return JSON.stringify(String(value ?? "")).slice(1, -1);
  });

  return JSON.parse(json);
};

export class TripettoStore {
  selectedCategoryIdsMap = {};
  selectedSubCategoryIdsMap = {};
  fieldTypeMap = {};

  setCategoryId(nodeId, value) {
    if (this.selectedCategoryIdsMap[nodeId] !== value) {
      this.selectedCategoryIdsMap[nodeId] = value;
    }
  }
  setSubCategoryId(nodeId, value) {
    if (this.selectedSubCategoryIdsMap[nodeId] !== value) {
      this.selectedSubCategoryIdsMap[nodeId] = value;
    }
  }
  removeCategoryId(nodeId) {
    delete this.selectedCategoryIdsMap[nodeId];
  }
  removeSubCategoryId(nodeId) {
    delete this.selectedSubCategoryIdsMap[nodeId];
  }
  registerFieldType(nodeId, fieldType) {
    if (this.fieldTypeMap[nodeId] !== fieldType) {
      this.fieldTypeMap[nodeId] = fieldType;
    }
  }

  constructor() {
    makeObservable(this, {
      selectedCategoryIdsMap: observable,
      selectedSubCategoryIdsMap: observable,
      setCategoryId: action,
      setSubCategoryId: action,
      removeCategoryId: action,
      removeSubCategoryId: action,
      fieldTypeMap: observable,
      registerFieldType: action,
    });
  }

  listCheckboxesFields() {
    return convertDataAtsToTripetto(
      atsStore.integratedAtsFields,
      convertCustomFieldsToTripetto(atsStore.customFields, checkboxesAtsFields, Enum.SurveyCustomFieldType.List, true),
      atsStore.customFields
    );
  }

  listDropdownFields() {
    return convertDataAtsToTripetto(
      atsStore.integratedAtsFields,
      convertCustomFieldsToTripetto(atsStore.customFields, dropDownAtsFields, Enum.SurveyCustomFieldType.List),
      atsStore.customFields
    );
  }

  listTextFields() {
    return convertDataAtsToTripetto(
      atsStore.integratedAtsFields,
      convertCustomFieldsToTripetto(atsStore.customFields, inputAtsFields, Enum.SurveyCustomFieldType.Text),
      atsStore.customFields
    );
  }

  listPhoneFields() {
    return convertDataAtsToTripetto(atsStore.integratedAtsFields, phoneAtsFields);
  }

  listFileFields() {
    return convertDataAtsToTripetto(atsStore.integratedAtsFields, fileUploadAtsFields);
  }

  listNumberFields() {
    return convertDataAtsToTripetto(atsStore.integratedAtsFields, numberAtsFields);
  }

  listEmailFields() {
    return convertDataAtsToTripetto(atsStore.integratedAtsFields, emailAtsFields);
  }

  listScaleFields() {
    return convertDataAtsToTripetto(atsStore.integratedAtsFields, scaleAtsFields);
  }

  listDateFields() {
    return convertDataAtsToTripetto(
      atsStore.integratedAtsFields,
      convertCustomFieldsToTripetto(atsStore.customFields, dateAtsFields, Enum.SurveyCustomFieldType.Date),
      atsStore.customFields
    );
  }

  listLinkedInFields() {
    return convertDataAtsToTripetto(atsStore.integratedAtsFields, linkedinAtsFields);
  }

  listCountries() {
    return (
      lookupStore.countries?.map((item) => ({
        name: item.name,
        value: item.code
      })) || []
    );
  }

  listWorkTypes() {
    return (
      lookupStore.currentWorkTypes?.map((item) => ({
        name: item.name,
        value: item.workTypeId.toString()
      })) || []
    );
  }

  listPayRates() {
    return (
      Enum.OptionSalaryRate?.map((item) => ({
        name: item.text,
        value: item.value
      })) || []
    );
  }

  listNoticePeriodFields() {
    return (
      Enum.OptionNoticePeriod?.map((item) => ({
        name: item.text,
        value: item.value
      })) || []
    );
  }

  // Status options come from the live JobAdder candidate status lookup.
  listStatusFields() {
    return (
      lookupStore.candidateStatuses
        ?.filter((item) => item.statusId)
        .map((item) => ({
          name: item.name,
          value: item.statusId.toString()
        })) || []
    );
  }

  listContactStatusFields() {
    return (
      lookupStore.clientStatuses
        ?.filter((item) => item.statusId)
        .map((item) => ({
          name: item.name,
          value: item.statusId.toString()
        })) || []
    );
  }

  // Salutation options come from the live JobAdder candidate salutation lookup.
  listSalutationFields() {
    return (
      lookupStore.salutations?.map((item) => ({
        name: item.name,
        value: item.name
      })) || []
    );
  }

  listCustomFields() {
    return (atsStore.customFields.length > 0 ? atsStore.customFields : lookupStore.customFields)
  }

  // Status options come from the live JobAdder candidate status lookup.
  listStatusFields() {
    return (
      lookupStore.candidateStatuses
        ?.filter((item) => item.statusId)
        .map((item) => ({
          name: item.name,
          value: item.statusId.toString()
        })) || []
    );
  }

  // Salutation  options come from the live JobAdder candidate status lookup
  listSalutationFields() {
    return (
      lookupStore.salutations?.map((item) => ({
        name: item.name,
        value: item.name
      })) || []
    );
  }

  listSkillCategories() {
    return [
      ...lookupStore.skillCategories?.map(c => ({
        name: c.name,
        value: c.categoryId.toString(),
      })) || []]
  }

  listSkillSubCategories(categoryId) {
    return lookupStore.skillSubCategories?.filter(sc => sc.categoryId === categoryId).map(sc => ({
      name: sc.name,
      value: sc.subCategoryId.toString(),
    })) || []
  }

  listSkills(categoryId, subCategoryId) {
    return lookupStore.skills?.filter(s => s.categoryId === categoryId && (s.subCategoryId === 0 || s.subCategoryId === subCategoryId)).map(s => ({
      name: s.name,
      value: s.skillId.toString(),
    })) || [];
  }

  listCheckboxSkillSubCategories(categoryIds) {
    return lookupStore.skillSubCategories?.filter(sc => categoryIds.includes(sc.categoryId)).map(sc => ({
      name: sc.name,
      value: sc.subCategoryId.toString(),
    })) || []
  }

  listCheckboxSkills(categoryIds, subCategoryIds) {
    return lookupStore.skills?.filter(s => categoryIds.includes(s.categoryId) && (s.subCategoryId === 0 || subCategoryIds.includes(s.subCategoryId))).map(s => ({
      name: s.name,
      value: s.skillId.toString(),
    })) || [];
  }

  listSuggestions() {
    return lookupStore.publicCompanies || [];
  }
}
export const tripettoStore = new TripettoStore();

const RADIOBUTTONS_BLOCK_TYPE = "@tripetto/block-radiobuttons";

// All three ROI-Tripetto forks (ROI-UI, ROI-Chat, ROI-Forms) now agree the radiobuttons block's
// static answer slot is referenced as "button" (the stock Tripetto convention). It briefly used
// "option" here and in ROI-UI (an inherited copy-paste artifact from the dropdown block, not a
// deliberate choice) before being reverted. Rather than track which specific legacy value a given
// definition might carry, normalize unconditionally to "button" — the block only ever has one
// static slot (the answer), so any static slot found here that isn't already "button" is by
// definition stale and safe to rewrite.
export const normalizeLegacyRadiobuttonSlots = (definition) => {
  const visitSection = (section) => {
    (section?.nodes || []).forEach(node => {
      if (node?.block?.type !== RADIOBUTTONS_BLOCK_TYPE || !Array.isArray(node.slots)) return;

      node.slots.forEach(slot => {
        if (slot?.kind === "static" && slot.reference !== "button") {
          slot.reference = "button";
        }
      });
    });

    // Branches hang off the section, not the node — a radio placed inside a branch is only
    // reachable through section.branches[].sections[].
    (section?.branches || []).forEach(branch => (branch?.sections || []).forEach(visitSection));
  };

  (definition?.sections || []).forEach(visitSection);

  return definition;
};

export const convertTripettoData = (data) => {
  let finalData = [];
  data.fields.forEach(element => {
    let tempData = {};
    tempData.value = element.value === 0 ? "0" : element.value ? element.value.toString() : "";
    tempData.type = element.type;
    tempData.slot = element.slot === "score" && (!element.string || element.string.trim() === "") 
        ? null 
        : element.slot || null;
    tempData.questionNodeId = element.node.id;
        
    // example data: ats-first_name-textinline
    // check if have ats on first data before -
    if (isNameHasAts(element.name) && element.type !== Constants.TripettoNodeType.CHECKBOXES) {
      tempData.atsField = getAtsTokenName(element.name);
      tempData.atsValue = tempData.value;
      // Use getSlotName to strip any section alias prefix before splitting
      const slotName = getSlotName(element.name);
      const splitedName = slotName.split("-");
      tempData.fieldId = splitedName.length > 2 ? splitedName[splitedName.length - 1] : "";
    }

    // check if have fileupload in it,, (default stored in 'reference' key)
    if (element.reference && element.slot === Constants.TripettoNodeType.FILE) {
      // get only base64 raw string without type 'base64'
      tempData.fileData = element.reference.split('base64,')[1];
      tempData.contentType = convertBase64toFileObject(element.reference, element.value).type;
      tempData.category = element.name;

      // Find and attach DocumentExpiryDate from same node if exists
      const expiryDateField = data.fields.find(
        field => field.node.id === element.node.id && field.slot === "expiryDate"
      );
      if (expiryDateField?.value) {
        tempData.documentExpiryDate = expiryDateField.value;
      }
    }

    // set value by reference only for DROPDOWN OPTION
    if (element.reference && element.slot === "option") {
      if (isNameHasAts(element.name)) {
        tempData.atsValue = element.reference;
      }
      tempData.value = element.value;
    }

    // add question here
    tempData.question = element.node.props().name;

    // set checkboxes
    const isIntegratedCheckboxes = element.type === Constants.TripettoNodeType.CHECKBOXES && element.slot === "selection";
    
    if (isIntegratedCheckboxes) {      

      if (element.value) {
        const isNullReference = element.reference.includes("ats");
        const checkboxesAtsFields = isNullReference ? splitCheckboxesReference(element.reference) : undefined;
        let checkboxesValue = [];     

        if (element.value !== "") {
          checkboxesValue = element.value.split(",");
        }

        tempData.atsField = isNullReference ? checkboxesAtsFields.atsField : undefined;
        if (isSkillField(tempData.atsField)) {
          tempData.atsValue = isNullReference ? JSON.stringify(checkboxesAtsFields.atsValue) : undefined;
        } else {
          tempData.atsValue = isNullReference ? JSON.stringify(checkboxesValue) : undefined;
        }
        tempData.fieldId = isNullReference ? checkboxesAtsFields.atsFieldId : undefined;
        tempData.question = isNullReference ? element.name : element.node.props().name;
        tempData.value = JSON.stringify(checkboxesValue);
      }

    } else if (element.type === Constants.TripettoNodeType.CHECKBOXES && !element.isMapped) {
      let groupedCheckboxes = data.fields.filter(field => field.node.id === element.node.id);
      let checkboxesValue = groupedCheckboxes
          .filter(field => field.value && field.name !== element.node.props().name)
          .map(field => field.name);
      groupedCheckboxes.forEach(field => field.isMapped = true);
      tempData.value = JSON.stringify(checkboxesValue);
      finalData.push(tempData);
    }

    // Add score property if slot is "score" and has a valid string value
    if (element.slot === "score" && element.string && element.string.trim() !== "") {
      tempData.score = parseInt(element.string, 10);
      tempData.value = tempData.score.toString();
    }
    // Special handling for date fields
    if (element.type === Constants.TripettoNodeType.DATE) {
      if (isNameHasAts(element.name)) {
        tempData.atsField = getAtsTokenName(element.name);
        tempData.atsValue = element.string;
      }
      tempData.value = element.string;
    }

    if (element.slot === "signature") {
      tempData.value = element.reference;
    }

    // tripetto blocks
    if (element.type) {
      switch(element.type) {
        case Constants.TripettoNodeType.MULTI_SELECT:
          if (!element.isMapped) {
            let groupedCheckboxes = data.fields.filter(field => field.node.id === element.node.id);
            let checkboxesValue = groupedCheckboxes
                .filter(field => field.value && field.name !== element.node.props().name)
                .map(field => field.name);
            groupedCheckboxes.forEach(field => field.isMapped = true);
            tempData.value = JSON.stringify(checkboxesValue);
            finalData.push(tempData);
          }
          break;
        case Constants.TripettoNodeType.MATRIX:
          if (!element.isMapped) {
            let groupedCheckboxes = data.fields.filter(field => field.node.id === element.node.id);
            let checkboxesValue = groupedCheckboxes
                .filter(field => field.name !== element.node.props().name)
                .map(field => `${field.name}-${field.value || ""}`);
            groupedCheckboxes.forEach(field => field.isMapped = true);
            tempData.value = JSON.stringify(checkboxesValue);
            finalData.push(tempData);
          }
          break;
        case Constants.TripettoNodeType.RANKING:
          if (!element.isMapped) {
            let groupedCheckboxes = data.fields.filter(field => field.node.id === element.node.id);
            let checkboxesValue = groupedCheckboxes
                .filter(field => field.name !== element.node.props().name)
                .map(field => field.value);
            groupedCheckboxes.forEach(field => field.isMapped = true);
            tempData.slot = element.slot.split(":")[0];
            tempData.value = JSON.stringify(checkboxesValue);
            finalData.push(tempData);
          }
          break;
        case Constants.TripettoNodeType.MULTIPLE_CHOICE:
          if (!element.isMapped && element.slot !== "choice") {
            let groupedCheckboxes = data.fields.filter(field => field.node.id === element.node.id);
            let checkboxesValue = groupedCheckboxes
                .filter(field => field.value && field.name !== element.node.props().name)
                .map(field => field.name);
            groupedCheckboxes.forEach(field => field.isMapped = true);
            tempData.value = JSON.stringify(checkboxesValue);
            finalData.push(tempData);
          }
          break;
      }
    }
    
    // Skip expiryDate slot since it's already embedded in the file object as DocumentExpiryDate
    const isExpiryDateSlot = element.type === Constants.TripettoNodeType.FILE && element.slot === "expiryDate";

    if (!isExpiryDateSlot && (isIntegratedCheckboxes ||
      element.type === Constants.TripettoNodeType.DROPDOWN ||
      element.type === Constants.TripettoNodeType.EMAIL ||
      element.type === Constants.TripettoNodeType.FILE ||
      element.type === Constants.TripettoNodeType.NUMBER ||
      element.type === Constants.TripettoNodeType.PHONE ||
      element.type === Constants.TripettoNodeType.TEXT ||
      element.type === Constants.TripettoNodeType.SCALE ||
      element.type === Constants.TripettoNodeType.DATE ||
      element.type === Constants.TripettoNodeType.LINKEDIN ||
      element.type === Constants.TripettoNodeType.SIGNATURE ||
      element.type === Constants.TripettoNodeType.PASSWORD ||
      element.type === Constants.TripettoNodeType.PICTURE_CHOICE ||
      element.type === Constants.TripettoNodeType.RADIOBUTTONS ||
      element.type === Constants.TripettoNodeType.RATING ||
      element.type === Constants.TripettoNodeType.TEXTAREA ||
      element.type === Constants.TripettoNodeType.URL ||
      element.type === Constants.TripettoNodeType.YES_NO ||
      element.type === Constants.TripettoNodeType.HIDDEN_FIELD ||
      element.type === Constants.TripettoNodeType.VARIABLE ||
      element.type === Constants.TripettoNodeType.CALCULATOR ||
      element.type === Constants.TripettoNodeType.EVALUATE ||
      element.type === Constants.TripettoNodeType.REGEX ||
      element.type === Constants.TripettoNodeType.SETTER ||
      (element.slot === "choice" && element.type === Constants.TripettoNodeType.MULTIPLE_CHOICE) ||
      (element.slot === "score" && element.string && element.string.trim() !== ""))) {
      finalData.push(tempData);
    }
  });

  return finalData;
};

export const convertCustomFieldsToTripetto = (customFields, fields, type, isMultiSelect = false) => {
  let dataFinal = [...fields];
  customFields.forEach(item => {
    if (item.type === type && item.isMultiSelect === isMultiSelect) {
      dataFinal.push(item.tokenVariable);
    }
  });
  return dataFinal;
};

export const convertDataAtsToTripetto = (atsListData, filterArray, customFields = []) => {
  // final data will look like this (for tripetto purpose)
  // [{
  //   label: "label test",
  //   value: "value test"
  // }]
  let finalData = [];

  atsListData.forEach(element => {
    if (filterArray.includes(element.tokenVariable)) {
      let object = {};
      object.label = element.tokenVariable === 'city' ? "Suburb" : element.tokenVariable === 'candidate_city' ? "Candidate Suburb" : element.displayName;
      object.value = `ats-${element.tokenVariable}`;
      finalData.push(object);
    }
  });
  customFields.length > 0 && customFields.forEach(element => {
    if (filterArray.includes(element.tokenVariable)) {
      let object = {};
      object.label = element.displayName;
      object.value = `ats-${element.tokenVariable}`;
      finalData.push(object);
    }
  });
  return finalData;
};
